import axios from 'axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put } from 'redux-saga/effects';

import { ApiUrl } from '../config';
import { extractWorkspaceId } from '../../utils/workspaceUtils';
import { setAllCampaigns, setSingleCampaign, setDashboardStats, setCampaignAnalytics, setCampaignTemplates, setCampaignTemplate, setMiloSession, addMiloMessage, setMiloSuggestions, setMiloLoading, clearMiloAgentActions, setPersonalizedMessageResult, setPersonalizationLoading, setStreamingText, setContentAnalysis, setAutoImproveResult, setAnalyzing, setAutoImproving, setCampaignContacts, setCampaignContactStats, setActivityFeed, setAIVariablesLoading, setAIVariablesResult, setAIVariableKeyStatus, setAIVariableColumns } from './index';

const CampaignApiUrl = `${ApiUrl}/campaign`;

function* getCall(path) {
  try {
    const response = yield call(() => axios.get(path));
    return { response };
  } catch (error) {
    return { error };
  }
}

function* postCall({ path, payload }) {
  try {
    const response = yield call(() => axios.post(path, payload));
    return { response };
  } catch (error) {
    return { error };
  }
}

function* putCall({ path, payload }) {
  try {
    const response = yield call(() => axios.put(path, payload));
    return { response };
  } catch (error) {
    return { error };
  }
}

function* deleteCall(path) {
  try {
    const response = yield call(() => axios.delete(path));
    return { response };
  } catch (error) {
    return { error };
  }
}

function* getAllCampaigns({ payload = {} }) {
  const params = new URLSearchParams();
  if (payload['status']) params.append('status', payload['status']);
  if (payload['type']) params.append('type', payload['type']);
  if (payload['search']) params.append('search', payload['search']);
  if (payload['page']) params.append('page', payload['page']);
  if (payload['limit']) params.append('limit', payload['limit']);

  const queryString = params.toString();
  const url = queryString ? `${CampaignApiUrl}?${queryString}` : CampaignApiUrl;

  const { error, response } = yield call(getCall, url);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch campaigns' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to fetch campaigns');
  } else if (response) {
    yield put(setAllCampaigns(response['data']['body'] || response['data']));
  }
}

function* getSingleCampaign({ payload }) {
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/detail/${payload}`);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to fetch campaign');
  } else if (response) {
    yield put(setSingleCampaign(response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data']));
  }
}

function* createCampaign({ payload }) {
  const { data, successCallback, failCallback, silent } = payload;
  const { error, response } = yield call(postCall, { path: CampaignApiUrl, payload: data });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to create campaign' });
    if (!silent) EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to create campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    yield put({ type: 'CREATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    if (!silent) EventBus.publish('success', 'Campaign created successfully');
  }
}

function* updateCampaign({ payload }) {
  const { id, data, successCallback, failCallback, silent } = payload;
  const { error, response } = yield call(putCall, { path: `${CampaignApiUrl}/detail/${id}`, payload: data });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to update campaign' });
    if (!silent) EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to update campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    if (!silent) yield put({ type: 'UPDATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    if (!silent) EventBus.publish('success', 'Campaign updated successfully');
  }
}

function* deleteCampaign({ payload }) {
  const { id, successCallback, failCallback } = payload;
  const { error, response } = yield call(deleteCall, `${CampaignApiUrl}/detail/${id}`);
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to delete campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to delete campaign');
  } else if (response) {
    yield put({ type: 'DELETE_CAMPAIGN_SUCCESS', payload: id });
    if (successCallback) successCallback();
    EventBus.publish('success', 'Campaign deleted successfully');
  }
}

function* duplicateCampaign({ payload }) {
  const { id, name, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/detail/${id}/duplicate`, payload: { name } });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to duplicate campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to duplicate campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    yield put({ type: 'CREATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    EventBus.publish('success', 'Campaign duplicated successfully');
  }
}

function* launchCampaign({ payload }) {
  const { id, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/detail/${id}/launch`, payload: {} });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to launch campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to launch campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    yield put({ type: 'UPDATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    EventBus.publish('success', 'Campaign launched successfully');
  }
}

function* pauseCampaign({ payload }) {
  const { id, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/detail/${id}/pause`, payload: {} });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to pause campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to pause campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    yield put({ type: 'UPDATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    EventBus.publish('success', 'Campaign paused successfully');
  }
}

function* resumeCampaign({ payload }) {
  const { id, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/detail/${id}/resume`, payload: {} });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to resume campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to resume campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    yield put({ type: 'UPDATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    EventBus.publish('success', 'Campaign resumed successfully');
  }
}

function* completeCampaign({ payload }) {
  const { id, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/detail/${id}/complete`, payload: {} });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to complete campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to complete campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    yield put({ type: 'UPDATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    EventBus.publish('success', 'Campaign completed successfully');
  }
}

function* generateCampaign({ payload }) {
  const { prompt, type, goal, channels, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/generate`, payload: { prompt, type, goal, channels } });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to generate campaign' });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to generate campaign');
  } else if (response) {
    const campaign = response['data']['body']?.['campaign'] || response['data']['campaign'] || response['data'];
    yield put({ type: 'CREATE_CAMPAIGN_SUCCESS', payload: campaign });
    if (successCallback) successCallback(campaign);
    EventBus.publish('success', 'Campaign generated successfully');
  }
}

function* getDashboardStats() {
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/dashboard-stats`);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch dashboard stats' });
  } else if (response) {
    yield put(setDashboardStats(response['data']['body']?.['stats'] || response['data']['stats'] || response['data']));
  }
}

function* getCampaignAnalytics({ payload }) {
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/detail/${payload}/analytics`);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch analytics' });
  } else if (response) {
    yield put(setCampaignAnalytics(response['data']['body']?.['analytics'] || response['data']['analytics'] || response['data']));
  }
}

function* getCampaignTemplates({ payload }) {
  const url = payload ? `${CampaignApiUrl}/templates?category=${payload}` : `${CampaignApiUrl}/templates`;
  const { error, response } = yield call(getCall, url);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch templates' });
  } else if (response) {
    yield put(setCampaignTemplates(response['data']['body']?.['templates'] || response['data']['templates'] || response['data']));
  }
}

function* getCampaignTemplate({ payload }) {
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/templates/${payload}`);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch template' });
  } else if (response) {
    yield put(setCampaignTemplate(response['data']['body']?.['template'] || response['data']['template'] || response['data']));
  }
}

// Milo AI Chat Sagas
function* startMiloSession({ payload }) {
  const { campaignId, successCallback, failCallback } = payload || {};
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/milo/start`, payload: { campaignId } });
  if (error) {
    if (failCallback) failCallback(error);
    yield put(setMiloLoading(false));
  } else if (response) {
    const body = response['data']['body'] || response['data'];
    yield put(setMiloSession({ sessionId: body['sessionId'], campaignId, greeting: body['greeting'], suggestions: body['suggestions'], messages: body['messages'] || [] }));
    if (successCallback) successCallback(body);
  }
}

function* sendMiloMessage({ payload }) {
  const { sessionId, message, campaignId, onCampaignDataUpdate, successCallback, failCallback } = payload;

  // Add user message immediately
  yield put(addMiloMessage({ id: `user_${Date.now()}`, role: 'user', content: message, timestamp: new Date().toISOString() }));
  yield put(setMiloLoading(true));

  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/milo/message`, payload: { sessionId, message, campaignId } });
  if (error) {
    if (failCallback) failCallback(error);
    yield put(addMiloMessage({ id: `error_${Date.now()}`, role: 'assistant', content: 'Sorry, something went wrong. Please try again.', timestamp: new Date().toISOString() }));
    yield put(setMiloLoading(false));
    yield put(clearMiloAgentActions());
  } else if (response) {
    const body = response['data']['body'] || response['data'];
    const assistantMessage = { id: `assistant_${Date.now()}`, role: 'assistant', content: body['response'], toolsUsed: body['toolsUsed'] || [], timestamp: new Date().toISOString() };
    yield put(addMiloMessage(assistantMessage));
    if (body['suggestions']) yield put(setMiloSuggestions(body['suggestions']));
    yield put(clearMiloAgentActions());

    // Notify parent of campaign data updates
    if (body['toolCalls'] && onCampaignDataUpdate) {
      body['toolCalls'].forEach(call => onCampaignDataUpdate(call['name'], call['arguments']));
    }

    if (successCallback) successCallback(body);
  }
}

function* clearMiloConversation({ payload }) {
  const { sessionId, campaignId, successCallback, failCallback } = payload || {};
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/milo/clear`, payload: { sessionId, campaignId } });
  if (error) {
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put({ type: 'RESET_MILO_SESSION' });
    if (successCallback) successCallback(response['data']);
  }
}

function* generateMiloTemplate({ payload }) {
  const { type, stepType, pitch, campaignId, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/milo/generate-template`, payload: { type, stepType, pitch, campaignId } });
  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to generate content');
  } else if (response) {
    const body = response['data']['body'] || response['data'];
    if (body['success'] || response['data']['success']) {
      if (successCallback) successCallback(body);
      EventBus.publish('success', 'Content generated successfully');
    } else {
      if (failCallback) failCallback(body);
      EventBus.publish('error', body['message'] || 'Failed to generate content');
    }
  }
}

function* generatePersonalizedMessage({ payload }) {
  const { campaignId, nodeId, contactId, type, prompt, pitch, nodeConfig, successCallback, failCallback } = payload;
  yield put(setPersonalizationLoading(true));

  const { error, response } = yield call(postCall, {
    path: `${CampaignApiUrl}/milo/generate-message`,
    payload: { campaignId, nodeId, contactId, type, prompt, pitch, nodeConfig }
  });

  if (error) {
    if (failCallback) failCallback(error);
    yield put(setPersonalizationLoading(false));
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to generate personalized message');
  } else if (response) {
    const body = response['data']['body'] || response['data'];
    if (body['success']) {
      yield put(setPersonalizedMessageResult(body));
      if (successCallback) successCallback(body);
    } else {
      if (failCallback) failCallback(body);
      EventBus.publish('error', body['message'] || 'Failed to generate personalized message');
    }
    yield put(setPersonalizationLoading(false));
  }
}

// Test campaign - send test email to specified address
function* sendTestCampaign({ payload }) {
  const { campaignId, testEmail, fromIntegrationId, contactId, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/detail/${campaignId}/test`, payload: { testEmail, fromIntegrationId, contactId } });
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_TEST_CAMPAIGN_RESPONSE', payload: { success: false, error: error?.['response']?.['data']?.['message'] || 'Failed to send test email' } });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to send test email');
  } else if (response) {
    yield put({ type: 'SET_TEST_CAMPAIGN_RESPONSE', payload: { success: true, error: null } });
    if (successCallback) successCallback(response['data']);
  }
}

// Test single node (email, sms, voice, whatsapp, linkedin)
function* sendTestNode({ payload }) {
  const { campaignId, nodeId, channel, testDestination, fromIntegrationId, contactId, content, isAIMode, successCallback, failCallback } = payload;
  yield put({ type: 'SET_TEST_CAMPAIGN_PROGRESS', payload: { status: 'started', totalSteps: 1, currentStep: 0, stepProgress: [], loading: true } });

  const { error, response } = yield call(postCall, {
    path: `${CampaignApiUrl}/detail/${campaignId}/test-node`,
    payload: { nodeId, channel, testDestination, fromIntegrationId, contactId, content, isAIMode }
  });

  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_TEST_CAMPAIGN_PROGRESS', payload: { status: 'failed', loading: false, error: error?.['response']?.['data']?.['message'] || 'Failed to send test' } });
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to send test');
  } else if (response) {
    const result = response['data']['body'] || response['data'];
    yield put({ type: 'SET_TEST_CAMPAIGN_PROGRESS', payload: { status: 'completed', loading: false, success: true, stepProgress: [result] } });
    if (successCallback) successCallback(result);
    EventBus.publish('success', `Test ${channel} sent successfully`);
  }
}

// Streaming version using EventSource
function* generatePersonalizedMessageStream({ payload }) {
  const { campaignId, nodeId, contactId, type, prompt, pitch, nodeConfig, onChunk, successCallback, failCallback } = payload;
  yield put(setPersonalizationLoading(true));
  yield put(setStreamingText(''));

  try {
    // Build query params for streaming endpoint
    const params = new URLSearchParams({
      type: type || '',
      prompt: prompt || '',
      nodeId: nodeId || '',
      contactId: contactId || '',
      campaignId: campaignId || '',
      pitch: JSON.stringify(pitch || {}),
      nodeConfig: JSON.stringify(nodeConfig || {})
    });

    // Use a channel to handle streaming events
    const streamUrl = `${CampaignApiUrl}/milo/generate-message-stream?${params.toString()}`;

    // Get auth token and workspace ID for the fetch request
    const token = localStorage.getItem('token');
    const workspaceId = extractWorkspaceId(window.location.pathname) || localStorage.getItem('workspaceId');

    // Since we can't easily use EventSource in a saga, we'll use fetch with a manual reader
    const response = yield call(fetch, streamUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/event-stream',
        'Authorization': `bearer ${token}`,
        'x-workspace-id': workspaceId || ''
      }
    })

    if (!response['ok']) throw new Error('Failed to start streaming');

    let fullText = '';
    const decoder = new TextDecoder();
    const reader = response.body.getReader();
    while (true) {
      const { done, value } = yield call([reader, reader.read]);
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);

            if (parsed['type'] === 'chunk' && parsed['text']) {
              fullText += parsed['text'];
              yield put(setStreamingText(fullText));
              if (onChunk) onChunk(parsed['text'], fullText);
            } else if (parsed['type'] === 'complete' && parsed['success']) {
              yield put(setPersonalizedMessageResult({
                success: true,
                body: parsed['body'],
                nodeId: parsed['nodeId'],
                subject: parsed['subject'],
                message: parsed['message']
              }));
              if (successCallback) successCallback({
                success: true,
                body: parsed['body'],
                nodeId: parsed['nodeId'],
                subject: parsed['subject'],
                message: parsed['message']
              });
            } else if (parsed['type'] === 'error') {
              if (failCallback) failCallback(parsed['error']);
              EventBus.publish('error', parsed['error'] || 'Streaming error');
            }
          } catch (e) {
            // Ignore parse errors for incomplete chunks
          }
        }
      }
    }
  } catch (error) {
    console.error('Streaming error:', error);
    if (failCallback) failCallback(error);
    EventBus.publish('error', error['message'] || 'Failed to generate personalized message');
  } finally {
    yield put(setPersonalizationLoading(false));
  }
}

// Content Analysis - analyze email/message and get confidence score
function* analyzeContent({ payload, callback }) {
  yield put(setAnalyzing(true));
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/analyze`, payload });
  if (error) {
    if (callback) callback(null, error);
    yield put(setAnalyzing(false));
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to analyze content');
  } else if (response) {
    const analysis = response['data']['body']?.['analysis'] || response['data']['analysis'];
    yield put(setContentAnalysis(analysis));
    if (callback) callback(analysis, null);
  }
}

// Auto-improve - iterate until target score is met
function* autoImproveContent({ payload, callback }) {
  yield put(setAutoImproving(true));
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/auto-improve`, payload });
  if (error) {
    if (callback) callback(null, error);
    yield put(setAutoImproving(false));
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to auto-improve content');
  } else if (response) {
    const result = response['data']['body'] || response['data'];
    yield put(setAutoImproveResult(result));
    if (result['targetMet']) EventBus.publish('success', `Score improved to ${result['finalScore']}/100`);
    else EventBus.publish('info', `Best score: ${result['finalScore']}/100 (target: ${payload['targetScore'] || 80})`);
    if (callback) callback(result, null);
  }
}

function* getCampaignContacts({ payload }) {
  const { id, page = 1, limit = 20, filters } = payload;
  const params = new URLSearchParams({ page, limit });
  if (filters) params.append('filters', JSON.stringify(filters));

  const { error, response } = yield call(getCall, `${CampaignApiUrl}/detail/${id}/contacts?${params.toString()}`);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch campaign contacts' });
  } else if (response) {
    yield put(setCampaignContacts(response['data']['body'] || response['data']));
  }
}

function* getCampaignContactStats({ payload }) {
  const id = payload['id'] || payload;
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/detail/${id}/contact-stats`);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch contact stats' });
  } else if (response) {
    yield put(setCampaignContactStats(response['data']['body'] || response['data']));
  }
}

function* getActivityFeedSaga({ payload }) {
  const { id, page = 1, limit = 20 } = payload;
  const params = new URLSearchParams({ page, limit });
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/detail/${id}/activity-feed?${params.toString()}`);
  if (error) {
    yield put({ type: 'SET_CAMPAIGN_ERROR', payload: error?.['response']?.['data']?.['message'] || 'Failed to fetch activity feed' });
  } else if (response) {
    yield put(setActivityFeed(response['data']['body']?.['activities'] || response['data']['activities'] || []));
  }
}

// ─── AI Variables Sagas ───────────────────────────────────────────────────────

function* runAIVariablesSaga({ payload }) {
  const { contacts, filters, useAllContacts, limit, prompt, model, columnName, campaignId, successCallback, failCallback } = payload;
  yield put(setAIVariablesLoading(true));
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/ai-variables/run`, payload: { contacts, filters, useAllContacts, limit, prompt, model, columnName, campaignId } });
  if (error) {
    yield put(setAIVariablesLoading(false));
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to run AI Variables');
  } else if (response) {
    const body = response['data']['body'] || response['data'];
    if (body['success']) {
      yield put(setAIVariablesResult({ columnName: body['columnName'], results: body['results'] }));
      if (successCallback) successCallback(body);
    } else {
      yield put(setAIVariablesLoading(false));
      if (failCallback) failCallback(body);
      EventBus.publish('error', body['message'] || 'Failed to run AI Variables');
    }
  }
}

function* saveAIVariableKeysSaga({ payload }) {
  const { openai, anthropic, xai, deepseek, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: `${CampaignApiUrl}/ai-variables/save-keys`, payload: { openai, anthropic, xai, deepseek } });
  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', 'Failed to save API keys');
  } else if (response) {
    EventBus.publish('success', 'API keys saved securely');
    // Refresh key status
    yield put({ type: 'GET_AI_VARIABLE_KEY_STATUS' });
    if (successCallback) successCallback();
  }
}

function* getAIVariableKeyStatusSaga() {
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/ai-variables/key-status`);
  if (!error && response) {
    const body = response['data']['body'] || response['data'];
    if (body['success']) yield put(setAIVariableKeyStatus(body['status']));
  }
}

function* getAIVariableColumnsSaga({ payload }) {
  const { campaignId } = payload;
  if (!campaignId) return;
  const { error, response } = yield call(getCall, `${CampaignApiUrl}/ai-variables/columns/${campaignId}`);
  if (!error && response) {
    const body = response['data']['body'] || response['data'];
    if (body['success'] && body['columns']) {
      // Convert stored { columnName: { contactId: value } } to Redux state format
      yield put(setAIVariableColumns(body['columns']));
    }
  }
}

export default function* campaignSagas() {
  yield all([
    takeEvery('GET_ALL_CAMPAIGNS', getAllCampaigns),
    takeEvery('GET_SINGLE_CAMPAIGN', getSingleCampaign),
    takeEvery('CREATE_CAMPAIGN', createCampaign),
    takeEvery('UPDATE_CAMPAIGN', updateCampaign),
    takeEvery('DELETE_CAMPAIGN', deleteCampaign),
    takeEvery('DUPLICATE_CAMPAIGN', duplicateCampaign),
    takeEvery('LAUNCH_CAMPAIGN', launchCampaign),
    takeEvery('PAUSE_CAMPAIGN', pauseCampaign),
    takeEvery('RESUME_CAMPAIGN', resumeCampaign),
    takeEvery('COMPLETE_CAMPAIGN', completeCampaign),
    takeEvery('GENERATE_CAMPAIGN', generateCampaign),
    takeEvery('GET_DASHBOARD_STATS', getDashboardStats),
    takeEvery('GET_CAMPAIGN_ANALYTICS', getCampaignAnalytics),
    takeEvery('GET_CAMPAIGN_TEMPLATES', getCampaignTemplates),
    takeEvery('GET_CAMPAIGN_TEMPLATE', getCampaignTemplate),
    takeEvery('START_MILO_SESSION', startMiloSession),
    takeEvery('SEND_MILO_MESSAGE', sendMiloMessage),
    takeEvery('CLEAR_MILO_CONVERSATION', clearMiloConversation),
    takeEvery('GENERATE_MILO_TEMPLATE', generateMiloTemplate),
    takeEvery('GENERATE_PERSONALIZED_MESSAGE', generatePersonalizedMessage),
    takeEvery('GENERATE_PERSONALIZED_MESSAGE_STREAM', generatePersonalizedMessageStream),
    takeEvery('SEND_TEST_CAMPAIGN', sendTestCampaign),
    takeEvery('SEND_TEST_NODE', sendTestNode),
    takeEvery('ANALYZE_CONTENT', analyzeContent),
    takeEvery('AUTO_IMPROVE_CONTENT', autoImproveContent),
    takeEvery('GET_CAMPAIGN_CONTACTS', getCampaignContacts),
    takeEvery('GET_CAMPAIGN_CONTACT_STATS', getCampaignContactStats),
    takeEvery('GET_ACTIVITY_FEED', getActivityFeedSaga),
    takeEvery('RUN_AI_VARIABLES', runAIVariablesSaga),
    takeEvery('SAVE_AI_VARIABLE_KEYS', saveAIVariableKeysSaga),
    takeEvery('GET_AI_VARIABLE_KEY_STATUS', getAIVariableKeyStatusSaga),
    takeEvery('GET_AI_VARIABLE_COLUMNS', getAIVariableColumnsSaga),
  ]);
}
